import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";
import { Skull, Menu, X, Video, Swords, Wrench, Users, Trophy, User as UserIcon, LogOut, Crown, Ghost, MessageCircle } from "lucide-react";

const links = [
  { to: "/video-ideas", label: "Video Ideas", icon: Video },
  { to: "/challenges", label: "Challenges", icon: Swords },
  { to: "/builds", label: "Builds", icon: Wrench },
  { to: "/survivor-maker", label: "Survivor Maker", icon: Ghost },
  { to: "/entity", label: "The Entity", icon: MessageCircle },
  { to: "/community", label: "Community", icon: Users },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/5 bg-background/80 backdrop-blur-2xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 md:px-8">
        <Link to="/" data-testid="nav-logo" className="group flex shrink-0 items-center gap-2.5">
          <Skull className="h-7 w-7 text-primary transition-transform group-hover:scale-110" />
          <span className="whitespace-nowrap font-display text-2xl tracking-wider text-foreground">
            DBD <span className="text-primary">CREATOR HUB</span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {links.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-2 px-3.5 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                  active ? "text-primary" : "text-foreground/70 hover:text-primary"
                }`}
              >
                <l.icon className="h-4 w-4" />
                {l.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          {user?.is_pro ? (
            <Link to="/pricing" data-testid="nav-pro-badge" className="flex items-center gap-1.5 border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
              <Crown className="h-3.5 w-3.5" /> Pro
            </Link>
          ) : (
            <Link to="/pricing" data-testid="nav-upgrade" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-primary hover:text-primary/80">
              <Crown className="h-4 w-4" /> Upgrade
            </Link>
          )}
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-profile" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-foreground/80 hover:text-primary">
                <UserIcon className="h-4 w-4" />
                {user.username}
              </Link>
              <Button data-testid="nav-logout" variant="ghost" size="sm" onClick={logout} className="rounded-sm text-foreground/60 hover:text-primary">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button data-testid="nav-signin" onClick={() => setAuthOpen(true)} className="rounded-sm bg-primary font-bold uppercase tracking-widest text-primary-foreground hover:bg-primary/90">
              Sign In
            </Button>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="text-foreground lg:hidden" onClick={() => setMobileOpen((v) => !v)}>
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {mobileOpen && (
        <div className="border-t border-white/5 bg-background/95 px-5 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-2 py-3 text-sm font-bold uppercase tracking-widest text-foreground/80">
                <l.icon className="h-4 w-4 text-primary" />
                {l.label}
              </Link>
            ))}
            <Link to="/pricing" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-2 py-3 text-sm font-bold uppercase tracking-widest text-primary">
              <Crown className="h-4 w-4 text-primary" />
              {user?.is_pro ? "Creator Pro ✓" : "Upgrade to Pro"}
            </Link>
            <div className="mt-2 border-t border-white/5 pt-3">
              {user ? (
                <div className="flex items-center justify-between">
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-foreground/80">
                    <UserIcon className="h-4 w-4 text-primary" /> {user.username}
                  </Link>
                  <Button variant="ghost" size="sm" onClick={() => { logout(); setMobileOpen(false); }}>
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button onClick={() => { setAuthOpen(true); setMobileOpen(false); }} className="w-full rounded-sm bg-primary font-bold uppercase tracking-widest">
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
};

export default Navbar;
